import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Bell,
  User,
  UserPlus,
  MessageSquare,
} from 'lucide-react';
import { ThemeToggle } from '../ThemeToggle';
import { auth, db } from '../../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, query as firestoreQuery, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useNotificationBell } from '../../hooks/useNotificationBell';
import { fetchSuggestedUsers, SuggestedUser } from '../../services/suggestionsService';
import { FollowService } from '../../services/followService';
import { SuggestedUsersModal } from '../SuggestedUsersModal';

interface Notification {
  id: string;
  read?: boolean;
  title?: string;
  message?: string;
  createdAt?: {
    seconds: number;
    nanoseconds: number;
  };
  recipientId?: string;
  [key: string]: any;
}


interface RightSidebarProps {
  className?: string;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({ className = '' }) => {
  const { unreadCount } = useNotifications();
  const { isAnimating } = useNotificationBell(unreadCount, false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string>('');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [whoToFollow, setWhoToFollow] = useState<SuggestedUser[]>([]);
  const [following, setFollowing] = useState<Record<string, boolean>>({});
  const [isSuggestionsModalOpen, setIsSuggestionsModalOpen] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const followUnsubs = useRef<Record<string, (() => void)[]>>({});

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!auth.currentUser?.uid) return;

    try {
      const ref = collection(db, 'notifications');
      const q = firestoreQuery(
        ref,
        where('recipientId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      const notifs = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Notification))
        .filter(notification => !notification.read)
        .slice(0, 10);

      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const handlePreviewEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (notifications.length === 0) {
      fetchNotifications();
    }
  }, [fetchNotifications, notifications.length]);

  const handlePreviewLeave = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = window.setTimeout(() => {
      // timeout callback
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Subscribe to auth and user profile
  useEffect(() => {
    let unsubProfile: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!u) {
        setAvatarUrl(null);
        setProfileName('');
        if (unsubProfile) { unsubProfile(); unsubProfile = null; }
        return;
      }
      setAvatarUrl(u.photoURL || null);
      try {
        setProfileName(u.displayName || u.email || '');
      } catch {
        setProfileName('');
      }

      try {
        const ref = doc(db, 'users', u.uid);
        unsubProfile = onSnapshot(ref, (snap) => {
          const data = snap.data() as {
            avatar?: string;
            displayName?: string;
          } | undefined;
          if (data?.avatar && typeof data.avatar === 'string') {
            setAvatarUrl(data.avatar);
          }
          if (data?.displayName) {
            setProfileName(String(data.displayName));
          }
        });
      } catch {
        // ignore
      }
    });
    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  // Load "Who to follow" suggestions from API
  useEffect(() => {
    let active = true;
    const loadSuggestions = async () => {
      try {
        setLoadingSuggestions(true);
        const currentUid = auth.currentUser?.uid;
        const suggestions = await fetchSuggestedUsers(5, currentUid);
        if (active) {
          setWhoToFollow(suggestions);
        }
      } catch (error) {
        console.error('Error loading suggestions:', error);
      } finally {
        if (active) setLoadingSuggestions(false);
      }
    };
    loadSuggestions();
    return () => { active = false; };
  }, [auth.currentUser?.uid]);

  // Subscribe to follow states for suggested users
  useEffect(() => {
    // Cleanup previous subscriptions
    Object.values(followUnsubs.current).forEach(unsubs => {
      unsubs.forEach(unsub => { try { unsub(); } catch {} });
    });
    followUnsubs.current = {};

    if (!whoToFollow.length || !auth.currentUser?.uid) return;

    whoToFollow.forEach(user => {
      const unsubs: (() => void)[] = [];
      
      // Subscribe to following state
      const unsubFollow = FollowService.subscribeIsFollowing(auth.currentUser!.uid, user.id, (f) => {
        setFollowing(prev => ({ ...prev, [user.id]: !!f }));
      });
      unsubs.push(unsubFollow);
      
      followUnsubs.current[user.id] = unsubs;
    });

    return () => {
      Object.values(followUnsubs.current).forEach(unsubs => {
        unsubs.forEach(unsub => { try { unsub(); } catch {} });
      });
    };
  }, [whoToFollow, auth.currentUser?.uid]);

  const handleFollow = async (user: SuggestedUser) => {
    if (!auth.currentUser?.uid) {
      return;
    }
    
    const wasFollowing = !!following[user.id];
    const nowFollowing = !wasFollowing;
    
    // Optimistic update
    setFollowing(prev => ({ ...prev, [user.id]: nowFollowing }));

    try {
      const res = await FollowService.toggleFollow(auth.currentUser.uid, user.id);
      setFollowing(prev => ({ ...prev, [user.id]: res.following }));
    } catch (e) {
      // Revert on error
      setFollowing(prev => ({ ...prev, [user.id]: wasFollowing }));
    }
  };

  return (
    <aside className={`hidden xl:flex flex-col h-screen sticky top-0 border-l border-neutral-200 dark:border-white/[0.08] bg-white dark:bg-[#080A0F] ${className}`}>
      <div className="flex-1 py-6 px-4 space-y-6">
        {/* Top Row - Profile, Messages, Notification, Theme Buttons */}
        <div className="flex items-center justify-end gap-3">
          {/* Profile Button */}
          <Link to="/profile">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full overflow-hidden bg-violet-600 flex items-center justify-center text-white font-bold text-base hover:bg-violet-500 transition-colors ring-2 ring-purple-500/20"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : profileName ? (
                profileName.trim().charAt(0).toUpperCase()
              ) : (
                <User size={20} />
              )}
            </motion.div>
          </Link>

          {/* Messages Button */}
          <Link to="/messages" className="relative">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-2xl bg-[#121620] hover:bg-[#181D2A] border border-white/[0.08] flex items-center justify-center transition-colors"
            >
              <MessageSquare size={18} className="text-slate-400 hover:text-white" />
            </motion.div>
          </Link>

          {/* Notification Button */}
          <Link
            to="/notifications"
            className="relative"
            onMouseEnter={handlePreviewEnter}
            onMouseLeave={handlePreviewLeave}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-2xl bg-[#121620] hover:bg-[#181D2A] border border-white/[0.08] flex items-center justify-center transition-colors"
            >
              <motion.div
                animate={isAnimating ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 5, -5, 2, -2, 0],
                } : {}}
                transition={{
                  duration: 0.8,
                  ease: "easeInOut",
                  times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1],
                }}
              >
                <Bell
                  size={18}
                  className={`text-slate-400 hover:text-white ${isAnimating ? 'animate-pulse' : ''}`}
                />
              </motion.div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-pink-500 text-white text-[10px] leading-[16px] text-center font-semibold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </motion.div>
          </Link>

          {/* Theme Button */}
          <ThemeToggle />
        </div>

        {/* Who to Follow Section */}
        <div className="bg-neutral-100/80 dark:bg-[#121620] rounded-2xl p-4 shadow-charcoal border border-neutral-200 dark:border-white/[0.08] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-neutral-900 dark:text-white text-sm">Suggestion to follow</h3>
            <button 
              onClick={() => setIsSuggestionsModalOpen(true)}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-500 font-medium transition-colors flex items-center gap-1"
            >
              See All
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            {loadingSuggestions ? (
              // Loading skeleton
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between animate-pulse">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-800" />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-24" />
                      <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-800" />
                </div>
              ))
            ) : whoToFollow.length === 0 ? (
              <p className="text-xs text-neutral-500 dark:text-gray-400 text-center py-4">
                No suggestions available
              </p>
            ) : (
              whoToFollow.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between group"
                >
                  <Link 
                    to={`/profile/${user.username}`}
                    className="flex items-center space-x-3 flex-1 min-w-0"
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.displayName}
                        className="w-9 h-9 rounded-full object-cover border border-white/10"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-xs font-semibold">
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-neutral-900 dark:text-white truncate group-hover:text-purple-500 transition-colors">
                        {user.displayName}
                      </p>
                      <p className="text-[11px] text-neutral-500 dark:text-slate-400 truncate">
                        @{user.username}
                      </p>
                      {user.reason && (
                        <p className="text-[10px] text-purple-600 dark:text-purple-400 truncate font-medium">
                          {user.reason}
                        </p>
                      )}
                    </div>
                  </Link>
                  <button 
                    onClick={() => handleFollow(user)}
                    className={`px-3 py-1 rounded-xl text-xs font-semibold transition-all ${
                      following[user.id]
                        ? 'bg-purple-600/20 text-purple-600 dark:text-purple-300 border border-purple-500/30 hover:bg-purple-600/30'
                        : 'border border-neutral-300 dark:border-white/10 hover:border-purple-400 text-neutral-700 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-neutral-200/50 dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    {following[user.id] ? 'Following' : 'Follow'}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer - Sticks to bottom */}
      <div className="lg:fixed lg:bottom-0 py-2 pl-18 mt-auto">
        <div className="flex flex-nowrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-dark-500 px-2">
          <Link to="/terms" className="hover:underline">Terms</Link>
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <Link to="/community-guidelines" className="hover:underline">Guidelines</Link>
          <Link to="/help" className="hover:underline">Help</Link>
        </div>
        <p className="text-xs text-neutral-400 dark:text-dark-600 px-2 mt-2">
          © 2025 Crevvi. Creative Vibes Only.
        </p>
      </div>
      
      {/* Suggested Users Modal */}
      <SuggestedUsersModal 
        isOpen={isSuggestionsModalOpen} 
        onClose={() => setIsSuggestionsModalOpen(false)} 
      />
    </aside>
  );
};

export default RightSidebar;
